import api from './client'
import type { MedHistoryItem } from '@/components/medical/DatedMedItemsInput'
import type {
  Profile,
  EmergencyContact,
  KycDocument,
  MedicalCertificate,
  ProfileInsurance,
  Province,
  UserCertification,
  UserEquipment,
} from '@/types'

export interface DivemasterProfile {
  id: string
  userId: string
  isActive: boolean
  bioIt?: string | null
  bioEn?: string | null
  services: string[]
  agenciesTeaching: string[]
  languages: string[]
  maxGroupSize?: number | null
  pricePerDiveEurCents?: number | null
  pricePerCourseEurCents?: number | null
  primaryCertId?: string | null
  approvedAt?: string | null
  approvedBy?: string | null
  suspendedAt?: string | null
  suspendedReason?: string | null
  createdAt: string
  updatedAt: string
}

export interface AchievementDef {
  code: string
  titleIt: string
  titleEn: string
  descriptionIt: string
  descriptionEn: string
  icon: string
  category: 'dives' | 'depth' | 'certifications' | 'social' | 'content' | 'special'
  tier?: 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM'
}

export interface EarnedAchievement {
  id: string
  userId: string
  code: string
  earnedAt: string
  notifiedAt: string | null
  definition: AchievementDef | null
}

export interface ContactRequest {
  id: string
  fromUserId: string
  toUserId: string
  message: string | null
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'WITHDRAWN'
  createdAt: string
  resolvedAt: string | null
}

// ── Profile ────────────────────────────────────────────────────────────────────

export interface CareerRequirement {
  code: string
  ok: boolean | null
  required: unknown
  item?: string
}

export interface CareerStep {
  levelCode: string
  nameEn: string
  nameIt: string
  status: 'HELD' | 'NEXT' | 'LOCKED'
  missing: CareerRequirement[]
}

export interface CareerPath {
  agencyCode: string | null
  steps: CareerStep[]
  next: CareerStep | null
}

export interface ProfessionalCv {
  profile: { displayName: string | null; bio: string | null; country: string | null; city: string | null } | null
  certifications: { agency: string; levelCode: string | null; name: string; issuedAt: string | null; expiresAt: string | null; verified: boolean }[]
  memberships: { agencyCode: string; year: number; status: string; validUntil: string; current: boolean }[]
  dives: { total: number; validated: number } | null
  career: CareerPath | null
  specialConditions: SpecialConditionsComparison | null
  continuingEducation?: {
    count: number
    totalCredits: number
    overdue: number
    nextDueAt: string | null
    recent: { title: string; provider: string | null; credits: number | null; completedAt: string; dueAt: string | null }[]
  }
}

export interface SpecialConditionsComparison {
  required: number
  thresholds: { depthM?: number; visibilityM?: number; waterTempC?: number; night?: boolean; wreck?: boolean }
  count: number | null
  status: 'MET' | 'NOT_MET' | 'UNKNOWN' | 'NO_THRESHOLDS'
}

export interface ContinuingEducationEntry {
  id: string
  title: string
  provider: string | null
  credits: string | number | null
  completedAt: string
  dueAt: string | null
  notes: string | null
}

export interface ContinuingEducationInput {
  title: string
  provider?: string | null
  credits?: number | null
  completedAt: string
  dueAt?: string | null
  notes?: string | null
}

export const continuingEducationApi = {
  list: () =>
    api.get<{ entries: ContinuingEducationEntry[]; totalCredits: number }>('/v1/profile/me/continuing-education').then((r) => r.data),
  create: (data: ContinuingEducationInput) =>
    api.post<ContinuingEducationEntry>('/v1/profile/me/continuing-education', data).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/v1/profile/me/continuing-education/${id}`).then(() => undefined),
}

export const profileApi = {
  careerPath: (agency?: string) =>
    api.get<CareerPath>('/v1/profile/me/career-path', { params: { agency } }).then((r) => r.data),

  professionalCv: (agency?: string) =>
    api.get<ProfessionalCv>('/v1/profile/me/cv', { params: { agency } }).then((r) => r.data),

  getMyProfile: () =>
    api.get<Profile>('/v1/profiles/me').then((r) => r.data),

  updateMyProfile: (data: Partial<Profile>) =>
    api.put('/v1/profiles/me', data).then((r) => r.data),

  getProfile: (userId: string) =>
    api.get<Profile>(`/v1/profiles/${userId}`, { _skipToast: true } as any).then((r) => r.data),

  // ── Contatti (consenso reciproco diver→diver) ────────────────────────────────
  // Contatti sbloccati: 200 con i recapiti se esiste un ContactRequest ACCEPTED, 403 altrimenti.
  getContacts: (userId: string) =>
    api.get<{ userId: string; mobilePhone: string | null; homePhone: string | null; whatsapp: string | null }>(
      `/v1/profiles/${userId}/contacts`, { _skipToast: true } as any,
    ).then((r) => r.data),

  sendContactRequest: (toUserId: string, opts?: { message?: string; shareMobilePhone?: boolean; shareHomePhone?: boolean; shareWhatsapp?: boolean }) =>
    api.post<{ id: string; status: string }>('/v1/profile/contact-requests', { toUserId, ...opts }).then((r) => r.data),

  listContactRequests: (box: 'incoming' | 'outgoing' = 'incoming') =>
    api.get<{ box: string; requests: ContactRequest[] }>(`/v1/profile/contact-requests?box=${box}`).then((r) => r.data.requests),

  acceptContactRequest: (id: string) =>
    api.post<{ id: string; status: string }>(`/v1/profile/contact-requests/${id}/accept`, {}).then((r) => r.data),

  declineContactRequest: (id: string) =>
    api.post<{ id: string; status: string }>(`/v1/profile/contact-requests/${id}/decline`, {}).then((r) => r.data),

  withdrawContactRequest: (id: string) =>
    api.delete<{ id: string; status: string }>(`/v1/profile/contact-requests/${id}`).then((r) => r.data),

  listDivemasters: (): Promise<DivemasterProfile[]> =>
    api.get<{ items: DivemasterProfile[] }>('/v1/profile/divemasters')
      .then((r) => r.data.items ?? []),

  achievementsCatalog: () =>
    api.get<AchievementDef[]>('/v1/profile/achievements/catalog').then((r) => r.data),

  myAchievements: () =>
    api.get<EarnedAchievement[]>('/v1/profile/me/achievements').then((r) => r.data),

  // ── Certifications ───────────────────────────────────────────────────────────

  listCertifications: () =>
    api.get<UserCertification[]>('/v1/profile/me/certifications').then((r) => r.data),

  addCertification: (data: {
    agency: string
    certificationCode: string
    certificationName: string
    certNumber?: string
    issuedAt?: string
    expiresAt?: string
    certLevelId?: string
    instructorName?: string | null
    instructorNumber?: string | null
  }) => api.post<UserCertification>('/v1/profile/me/certifications', data).then((r) => r.data),

  getCertification: (id: string) =>
    api.get(`/v1/profile/me/certifications/${id}`).then((r) => r.data),

  getCertificationQrToken: (id: string) =>
    api.get<{ token: string; expiresAt: string }>(`/v1/profile/me/certifications/${id}/qr-token`).then((r) => r.data),

  getCheckinQr: () =>
    api.get<{ token: string; expiresAt: string; qrData: string }>('/v1/profile/me/checkin-qr').then((r) => r.data),

  verifyCheckinToken: (token: string) =>
    api.get<{
      valid: boolean
      error?: string
      userId?: string
      firstName?: string | null
      lastName?: string | null
      certifications?: Array<{ agency: string; name: string; code: string; verificationStatus: string }>
      kycStatus?: string
      medicalExpiry?: string | null
    }>(`/v1/profile/verify/checkin/${encodeURIComponent(token)}`).then((r) => r.data),

  getMedicalHistory: () =>
    api.get<{
      bloodType: string
      allergies: MedHistoryItem[]
      currentMeds: MedHistoryItem[]
      conditions: MedHistoryItem[]
      diveRestrictions: string[]
      emergencyNotes: string | null
      insuranceProvider: string | null
      insuranceNumber: string | null
      danMember: string | null
      certLevelDisplay: string | null
      shareEmergencyInDives?: boolean
      updatedAt?: string
    }>('/v1/profile/me/medical-history', { _skipToast: true } as any).then((r) => r.data),

  upsertMedicalHistory: (data: {
    bloodType?: string
    allergies?: (string | MedHistoryItem)[]
    currentMeds?: (string | MedHistoryItem)[]
    conditions?: (string | MedHistoryItem)[]
    diveRestrictions?: string[]
    emergencyNotes?: string | null
    insuranceProvider?: string | null
    insuranceNumber?: string | null
    danMember?: string | null
    certLevelDisplay?: string | null
    shareEmergencyInDives?: boolean
  }) => api.put('/v1/profile/me/medical-history', data).then((r) => r.data),

  // Tessera/QR di emergenza PERSISTENTE (non scade): genera/rigenera il token opaco.
  // Token corrente (get-or-create): da usare all'apertura della pagina, NON rigenera.
  getEmergencyToken: () =>
    api.get<{ token: string; qrData: string; issuedAt: string }>('/v1/profile/me/emergency-token').then((r) => r.data),
  regenerateEmergencyToken: () =>
    api.post<{ token: string; qrData: string; issuedAt: string }>('/v1/profile/me/emergency-token').then((r) => r.data),
  revokeEmergencyToken: () =>
    api.delete('/v1/profile/me/emergency-token').then((r) => r.data),

  getCertWalletQr: () =>
    api.get<{ token: string; expiresAt: string; qrData: string; certCount: number }>('/v1/profile/me/certifications/wallet-qr').then((r) => r.data),

  deleteCertification: (id: string) =>
    api.delete(`/v1/profile/me/certifications/${id}`),

  updateCertification: (id: string, data: {
    certNumber?: string | null
    issuedAt?: string | null
    expiresAt?: string | null
    instructorName?: string | null
    instructorNumber?: string | null
  }) => api.patch<UserCertification>(`/v1/profile/me/certifications/${id}`, data).then((r) => r.data),

  getCertCardUrls: (id: string) =>
    api.get<{ frontUrl?: string | null; backUrl?: string | null }>(`/v1/profile/me/certifications/${id}/card-urls`).then((r) => r.data),

  uploadCertCard: (id: string, data: { frontBase64: string; backBase64?: string; mimeType: 'image/jpeg' | 'image/png' | 'image/webp' }) =>
    api.post(`/v1/profile/me/certifications/${id}/card`, data).then((r) => r.data),

  verifyAgency: (data: { certificationId: string; agency: 'PADI' | 'SSI' | 'CMAS' | 'NAUI' | 'BSAC'; certNumber: string; lastName?: string }) =>
    api.post<{ verified: boolean; source: string; message: string; verifyUrl?: string }>(
      '/v1/profile/me/certifications/verify-agency', data
    ).then((r) => r.data),

  // ── Equipment ────────────────────────────────────────────────────────────────

  addEquipment: (data: {
    type: string
    brand?: string
    modelName?: string
    serialNumber?: string
    purchasedAt?: string
    notes?: string
  }) => api.post<UserEquipment>('/v1/profile/me/equipment', data).then((r) => r.data),

  updateEquipment: (id: string, data: {
    brand?: string
    modelName?: string
    serialNumber?: string
    purchasedAt?: string
    nextServiceDue?: string
    notes?: string
  }) => api.patch<UserEquipment>(`/v1/profile/me/equipment/${id}`, data).then((r) => r.data),

  deleteEquipment: (id: string) =>
    api.delete(`/v1/profile/me/equipment/${id}`),

  logEquipmentService: (id: string, data: { servicedAt: string; nextServiceDue?: string; notes?: string }) =>
    api.post<UserEquipment>(`/v1/profile/me/equipment/${id}/service`, data).then((r) => r.data),

  overdueEquipment: () =>
    api.get<UserEquipment[]>('/v1/profile/me/equipment/overdue').then((r) => r.data),

  // ── Rebreather units (ISO 24804-24808) ──────────────────────────────────────

  listRebreatherUnits: () =>
    api.get<RebreatherUnit[]>('/v1/profile/me/rebreathers').then((r) => r.data),

  addRebreatherUnit: (data: Omit<RebreatherUnit, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) =>
    api.post<RebreatherUnit>('/v1/profile/me/rebreathers', data).then((r) => r.data),

  updateRebreatherUnit: (id: string, data: Partial<Omit<RebreatherUnit, 'id' | 'userId' | 'createdAt' | 'updatedAt'>>) =>
    api.patch<RebreatherUnit>(`/v1/profile/me/rebreathers/${id}`, data).then((r) => r.data),

  deleteRebreatherUnit: (id: string) =>
    api.delete(`/v1/profile/me/rebreathers/${id}`),

  // ── Professional Diver License (Legge 9/2026) ───────────────────────────────

  getProfessionalLicense: () =>
    api.get<ProfessionalDiverLicense>('/v1/profile/me/professional-license', { _skipToast: true } as any)
      .then((r) => r.data)
      .catch((e) => { if (e?.response?.status === 404) return null; throw e }),

  upsertProfessionalLicense: (data: Partial<Omit<ProfessionalDiverLicense, 'id' | 'userId' | 'createdAt' | 'updatedAt'>>) =>
    api.patch<ProfessionalDiverLicense>('/v1/profile/me/professional-license', data).then((r) => r.data),

  deleteProfessionalLicense: () =>
    api.delete('/v1/profile/me/professional-license'),

  // ── Certificazioni/idoneità professionali (offshore) con scadenza ─────────────
  listProfessionalCerts: () =>
    api.get<ProfessionalCertification[]>('/v1/profile/me/professional-certs', { _skipToast: true } as any).then((r) => r.data),
  createProfessionalCert: (data: ProfessionalCertInput) =>
    api.post<ProfessionalCertification>('/v1/profile/me/professional-certs', data).then((r) => r.data),
  updateProfessionalCert: (id: string, data: Partial<ProfessionalCertInput>) =>
    api.patch<ProfessionalCertification>(`/v1/profile/me/professional-certs/${id}`, data).then((r) => r.data),
  deleteProfessionalCert: (id: string) =>
    api.delete(`/v1/profile/me/professional-certs/${id}`),

  // ── Emergency contact ────────────────────────────────────────────────────────

  getEmergencyContact: () =>
    api.get<EmergencyContact | null>('/v1/profile/me/emergency-contact')
      .then((r) => r.data ?? null),

  upsertEmergencyContact: (data: {
    firstName: string
    lastName: string
    relationship: string
    primaryPhone: string
    secondaryPhone?: string
    email?: string
    medicalNotes?: string
  }) => api.put<EmergencyContact>('/v1/profile/me/emergency-contact', data).then((r) => r.data),

  deleteEmergencyContact: () =>
    api.delete('/v1/profile/me/emergency-contact'),

  // ── KYC documents ────────────────────────────────────────────────────────────

  listKycDocuments: () =>
    api.get<KycDocument[]>('/v1/profile/me/kyc/documents').then((r) => r.data),

  uploadKycDocument: (data: {
    documentType: string
    frontBase64: string
    backBase64?: string
    mimeType: string
  }) => api.post<KycDocument>('/v1/profile/me/kyc/documents', data).then((r) => r.data),

  getKycDocumentUrls: (id: string) =>
    api.get<{ frontUrl: string | null; backUrl: string | null }>(
      `/v1/profile/me/kyc/documents/${id}/urls`,
    ).then((r) => r.data),

  deleteKycDocument: (id: string) =>
    api.delete(`/v1/profile/me/kyc/documents/${id}`),

  // ── Medical certificates ─────────────────────────────────────────────────────

  listMedicalCerts: () =>
    api.get<MedicalCertificate[]>('/v1/profile/me/medical-certs').then((r) => r.data),

  addMedicalCert: (data: {
    certType: string
    issuedAt: string
    expiresAt: string
    notes?: string
    documentBase64?: string
    documentMime?: string
  }) => api.post<MedicalCertificate>('/v1/profile/me/medical-certs', data).then((r) => r.data),

  getMedicalCertUrl: (id: string) =>
    api.get<{ url: string }>(`/v1/profile/me/medical-certs/${id}/url`).then((r) => r.data),

  deleteMedicalCert: (id: string) =>
    api.delete(`/v1/profile/me/medical-certs/${id}`),

  // ── Insurance ────────────────────────────────────────────────────────────────

  listInsurances: () =>
    api.get<ProfileInsurance[]>('/v1/profile/me/insurances').then((r) => r.data),

  addInsurance: (data: {
    company: string
    policyNumber: string
    coverageType: string
    expiresAt: string
    documentBase64?: string
    documentMime?: string
  }) => api.post<ProfileInsurance>('/v1/profile/me/insurances', data).then((r) => r.data),

  getInsuranceUrl: (id: string) =>
    api.get<{ url: string }>(`/v1/profile/me/insurances/${id}/url`).then((r) => r.data),

  deleteInsurance: (id: string) =>
    api.delete(`/v1/profile/me/insurances/${id}`),

  // ── ISTAT lookup ─────────────────────────────────────────────────────────────

  searchProvinces: (q: string) =>
    api.get<Province[]>('/v1/profile/lookup/provinces', { params: { q } }).then((r) => r.data),

  // ── Community search ─────────────────────────────────────────────────────────

  searchProfiles: (params: {
    q?: string
    profileType?: 'DIVER' | 'INSTRUCTOR' | 'DIVE_MASTER' | 'DIVE_DOCTOR'
    agency?: string
    certLevel?: string
    country?: string
    city?: string
    specialty?: string
    page?: number
    limit?: number
  }) => {
    const { profileType, ...rest } = params
    const queryParams = { ...rest, ...(profileType ? { type: profileType } : {}) }
    return api.get<{ data: Profile[]; meta: { total: number; page: number; limit: number; totalPages: number } }>(
      '/v1/profiles/search', { params: queryParams }
    ).then((r) => r.data)
  },

  searchDoctors: (params?: { country?: string; city?: string; page?: number; limit?: number }) =>
    api.get<{ data: Profile[]; meta: { total: number; page: number; limit: number; totalPages: number } }>(
      '/v1/profiles/search/doctors', { params }
    ).then((r) => r.data),

  searchInstructors: (params?: { agency?: string; certLevel?: string; country?: string; city?: string; page?: number; limit?: number }) =>
    api.get<{ data: Profile[]; meta: { total: number; page: number; limit: number; totalPages: number } }>(
      '/v1/profiles/search/instructors', { params }
    ).then((r) => r.data),

  updateSearchSettings: (data: {
    isSearchable?: boolean
    specialty?: string
    practiceLocation?: string
    contactVisibility?: 'PUBLIC' | 'CONNECTIONS' | 'PRIVATE'
  }) =>
    api.put('/v1/profiles/me/search-settings', data).then((r) => r.data),
}

export type ProfessionalDiverCategory = 'OTS_SHALLOW' | 'OTS_MEDIUM' | 'OTS_DEEP' | 'HYPERBARIC_TI'
export type ProfessionalDiverStatus = 'ACTIVE' | 'SUSPENDED' | 'REVOKED' | 'EXPIRED'
export type MedicalDoctorType = 'USMAF' | 'IPERBARICA' | 'SPECIALISTA'

export interface ProfessionalDiverLicense {
  id: string
  userId: string
  category: ProfessionalDiverCategory
  licenseNumber?: string | null
  issuingAuthority?: string | null
  issuedAt?: string | null
  expiresAt?: string | null
  medicalFitnessAt?: string | null
  medicalDoctorType?: MedicalDoctorType | null
  status: ProfessionalDiverStatus
  documentKey?: string | null
  notes?: string | null
  createdAt: string
  updatedAt: string
}

// Certificazioni/idoneità professionali (offshore) con scadenza
export type ProfessionalCertType =
  | 'MEDICAL_OTS' | 'MEDICAL_HSE_DIVING' | 'MEDICAL_OGUK_OEUK'
  | 'BOSIET_OPITO' | 'HUET_OPITO' | 'FOET_OPITO'
  | 'DIVER_MEDIC_DMT' | 'FIRST_AID_O2' | 'GWO' | 'MIST' | 'OTHER'

export interface ProfessionalCertification {
  id: string
  userId: string
  type: ProfessionalCertType
  name?: string | null
  issuingAuthority?: string | null
  certNumber?: string | null
  issuedAt?: string | null
  expiresAt?: string | null
  documentKey?: string | null
  notes?: string | null
  status: 'VALID' | 'EXPIRING' | 'EXPIRED'
  createdAt: string
  updatedAt: string
}

export interface ProfessionalCertInput {
  type: ProfessionalCertType
  name?: string | null
  issuingAuthority?: string | null
  certNumber?: string | null
  issuedAt?: string | null
  expiresAt?: string | null
  notes?: string | null
}

export type RebreatherUnitType = 'CCR' | 'SCR' | 'MCCR'

export interface RebreatherUnit {
  id: string
  userId: string
  brand: string
  model: string
  serialNumber?: string | null
  type: RebreatherUnitType
  lastServiceAt?: string | null
  nextServiceAt?: string | null
  certifiedUntil?: string | null
  notes?: string | null
  createdAt: string
  updatedAt: string
}

export type RoleRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED'
export type AllowedRole = 'INSTRUCTOR' | 'ASSISTANT_INSTRUCTOR' | 'DIVEMASTER' | 'DIVE_DOCTOR' | 'DIVE_CENTER' | 'TRAVEL_AGENCY' | 'ASSOCIATION' | 'ACADEMY' | 'UNDERWATER_PHOTOGRAPHER' | 'RESEARCH_CENTER'

export interface RoleRequest {
  id: string
  userId: string
  requestedRole: AllowedRole
  status: RoleRequestStatus
  motivation?: string | null
  resolvedBy?: string | null
  rejectionNote?: string | null
  requestedAt: string
  resolvedAt?: string | null
  profile?: { userId: string; firstName: string | null; lastName: string | null; displayName: string | null } | null
}

export const roleRequestsApi = {
  submit: (data: { requestedRole: AllowedRole; motivation?: string }) =>
    api.post<RoleRequest>('/v1/profile/role-requests', data).then((r) => r.data),

  myRequests: () =>
    api.get<RoleRequest[]>('/v1/profile/role-requests/me').then((r) => r.data),

  adminList: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get<{ data: RoleRequest[]; meta: { total: number; page: number; limit: number; totalPages: number } }>(
      '/v1/profile/admin/role-requests', { params }
    ).then((r) => r.data),

  approve: (id: string) =>
    api.post<RoleRequest>(`/v1/profile/admin/role-requests/${id}/approve`).then((r) => r.data),

  reject: (id: string, rejectionNote?: string) =>
    api.post<RoleRequest>(`/v1/profile/admin/role-requests/${id}/reject`, { rejectionNote }).then((r) => r.data),
}

// ── Professional types ────────────────────────────────────────────────────────

export type ProfessionalType =
  | 'INSTRUCTOR' | 'DIVE_DOCTOR' | 'UNDERWATER_PHOTOGRAPHER'
  | 'DIVEMASTER' | 'DIVE_CENTER' | 'TRAVEL_AGENCY' | 'ACADEMY'

export interface ProfessionalCard {
  userId: string
  displayName: string | null
  avatarKey: string | null
  avatarUrl: string | null
  country: string | null
  city: string | null
  profileType: string
  specialty: string | null
  practiceLocation: string | null
  instructorProfile?: {
    agencies: string[]
    maxCertLevel: string | null
    specialties: string[]
    teachingLangs: string[]
    isAvailableOnline: boolean
    dailyRateEurCents: number | null
    bioPublic: string | null
    avgRating: string | null
    reviewCount: number
  }
  photographerProfile?: {
    portfolioUrl: string | null
    equipment: string | null
    serviceAreas: string[]
    bioPublic: string | null
    avgRating: string | null
    reviewCount: number
  }
  doctorProfile?: {
    licenseNumber: string | null
    licenseCountry: string | null
    specializations: string[]
    institution: string | null
    city: string | null
    acceptsNewPatients: boolean
    bio: string | null
    avgRating: string | null
    reviewCount: number
  }
}

export interface ProfessionalsResponse {
  items: ProfessionalCard[]
  total: number
  page: number
  limit: number
}

export interface PhotographerProfileData {
  portfolioUrl: string | null
  equipment: string | null
  serviceAreas: string[]
  bioPublic: string | null
  isPublic: boolean
  avgRating: string | null
  reviewCount: number
}

export const professionalsApi = {
  list: (params: { type?: ProfessionalType; country?: string; city?: string; q?: string; page?: number; limit?: number } = {}) =>
    api.get<ProfessionalsResponse>('/v1/profile/professionals', { params }).then((r) => r.data),

  get: (userId: string) =>
    api.get<ProfessionalCard>(`/v1/profile/professionals/${userId}`).then((r) => r.data),

  getMyPhotographerProfile: () =>
    api.get<PhotographerProfileData>('/v1/profile/me/photographer').then((r) => r.data),

  upsertPhotographerProfile: (data: Partial<PhotographerProfileData>) =>
    api.put<PhotographerProfileData>('/v1/profile/me/photographer', data).then((r) => r.data),
}

// ── Visibilità del profilo ────────────────────────────────────────────────────
// I tre interruttori che l'informativa promette all'utente. Stavano sparsi fra
// PUT /v1/profiles/me e PUT /v1/profiles/me/search-settings, e nessuna schermata
// li mostrava.

export type ContactVisibility = 'PUBLIC' | 'CONNECTIONS' | 'PRIVATE'

export interface PrivacySettings {
  profileVisible: boolean
  searchable: boolean
  contactVisibility: ContactVisibility
  contactVisibilityOptions: ContactVisibility[]
  /** Cosa comporta ogni scelta, in parole: si mostra accanto all'interruttore. */
  spiegazioni: {
    profileVisible: string
    searchable: string
    contactVisibility: Record<ContactVisibility, string>
  }
}

export const privacySettingsApi = {
  get: () => api.get<PrivacySettings>('/v1/profiles/me/privacy').then((r) => r.data),

  update: (data: Partial<Pick<PrivacySettings, 'profileVisible' | 'searchable' | 'contactVisibility'>>) =>
    api.put<Pick<PrivacySettings, 'profileVisible' | 'searchable' | 'contactVisibility'>>(
      '/v1/profiles/me/privacy', data,
    ).then((r) => r.data),
}
